import { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface Booking {
  id: string;
  bookingid: string;
  guestname: string;
  roomid: string;
  checkin: string | null;
  checkout: string | null;
  status?: string;
}

interface Passport {
  id: number;
  reservationno: string;
  guest_index: number;
  guest_name?: string;
  roomid?: string;
  doc_type?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  date_of_birth?: string;
  place_of_birth?: string;
  passport_number?: string;
  address?: string;
  nationality?: string;
  date_of_expiry?: string;
  sex?: string;
  religion?: string;
  marital_status?: string;
  occupation?: string;
  spouse_name?: string;
  image_front?: string;
  image_back?: string;
}

interface RoomGroup {
  roomid: string;
  roomnumber: string | number;
  roomtype: string;
  status: string;
  guests: Passport[];
}

type Tab = "Today's Check-ins" | "Guest IDs" | "Upload ID";

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }: { status?: string }) {
  const map: Record<string, { color: string; bg: string }> = {
    confirmed:    { color: "#C9A84C", bg: "rgba(201,168,76,0.12)" },
    "checked-in": { color: "#34d399", bg: "rgba(52,211,153,0.12)" },
    "checked-out":{ color: "rgba(242,235,217,0.3)", bg: "rgba(148,163,184,0.12)" },
    pending:      { color: "#fbbf24", bg: "rgba(251,191,36,0.12)" },
  };
  const m = map[(status || "").toLowerCase()] || { color: "rgba(242,235,217,0.3)", bg: "rgba(148,163,184,0.12)" };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: m.bg, color: m.color, letterSpacing: 0.4 }}>
      {status || "—"}
    </span>
  );
}

function ImageViewer({ url, label }: { url?: string | null; label: string }) {
  const [open, setOpen] = useState(false);
  const empty = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      <div style={{ width: 130, height: 82, borderRadius: 8, background: "rgba(255,255,255,0.03)", border: "1.5px dashed rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 10, color: "rgba(242,235,217,0.35)" }}>No image</span>
      </div>
      <span style={{ fontSize: 10, color: "rgba(242,235,217,0.45)", fontWeight: 600 }}>{label}</span>
    </div>
  );
  if (!url) return empty;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
      <img
        src={url} alt={label}
        onClick={() => setOpen(true)}
        style={{ width: 130, height: 82, objectFit: "cover", borderRadius: 8, cursor: "zoom-in", border: "1.5px solid rgba(37,99,235,0.25)", background: "#1e293b" }}
        onError={e => { (e.target as HTMLImageElement).style.opacity = "0.3"; }}
      />
      <span style={{ fontSize: 10, color: "#C9A84C", fontWeight: 600 }}>{label}</span>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <img src={url} alt={label} style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12, boxShadow: "0 30px 70px rgba(0,0,0,0.7)" }} />
        </div>
      )}
    </div>
  );
}

function Field({ label, value, dir, wide }: { label: string; value?: string | null; dir?: string; wide?: boolean }) {
  return (
    <div style={{ gridColumn: wide ? "1 / -1" : undefined, minWidth: 0 }}>
      <div style={{ fontSize: 9, color: "rgba(242,235,217,0.45)", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12, color: value ? "#cbd5e1" : "#1e293b", direction: dir as any, wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );
}

function PassportCard({ passport }: { passport: Passport }) {
  const displayName = passport.full_name || [passport.first_name, passport.last_name].filter(Boolean).join(" ") || passport.guest_name || "—";
  const hasFront = !!passport.image_front;
  const hasBack  = !!passport.image_back;

  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "14px 16px", display: "flex", gap: 16 }}>
      {/* Images */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, flexShrink: 0 }}>
        <ImageViewer url={hasFront ? passport.image_front : null} label="Front" />
        <ImageViewer url={hasBack  ? passport.image_back  : null} label="Back" />
      </div>

      {/* Data */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px", alignContent: "start", minWidth: 0 }}>
        <Field label="Full Name"       value={displayName}              dir="rtl" wide />
        <Field label="ID / Passport #" value={passport.passport_number} />
        <Field label="Nationality"     value={passport.nationality}     />
        <Field label="Sex"             value={passport.sex}             />
        <Field label="Date of Birth"   value={passport.date_of_birth}   />
        <Field label="Place of Birth"  value={passport.place_of_birth}  dir="rtl" />
        <Field label="Expiry Date"     value={passport.date_of_expiry}  />
        <Field label="Religion"        value={passport.religion}        />
        <Field label="Marital Status"  value={passport.marital_status}  />
        <Field label="Occupation"      value={passport.occupation}      />
        {passport.spouse_name && <Field label="Spouse" value={passport.spouse_name} dir="rtl" />}
        {passport.address && <Field label="Address" value={passport.address} dir="rtl" wide />}
      </div>
    </div>
  );
}

function RoomSection({ room }: { room: RoomGroup }) {
  return (
    <div style={{ marginBottom: 20 }}>
      {/* Room header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(201,168,76,0.12)", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#C9A84C" }}>{room.roomnumber}</span>
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#F2EBD9" }}>Room {room.roomnumber}</div>
          <div style={{ fontSize: 11, color: "rgba(242,235,217,0.45)" }}>{room.roomtype || "Standard"}{room.guests.length > 0 ? ` · ${room.guests.length} guest${room.guests.length > 1 ? "s" : ""}` : ""}</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <StatusBadge status={room.status} />
        </div>
      </div>

      {/* Guests */}
      {room.guests.length === 0 ? (
        <div style={{ fontSize: 12, color: "rgba(242,235,217,0.35)", padding: "12px 16px", background: "rgba(255,255,255,0.02)", borderRadius: 8, border: "1px dashed rgba(255,255,255,0.06)" }}>
          No ID documents submitted for this room yet.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {room.guests.map((p, i) => (
            <div key={p.id}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#C9A84C", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
                Guest {i + 1} · {p.doc_type || "ID"}
              </div>
              <PassportCard passport={p} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReceptionDashboard() {
  const token = localStorage.getItem("ai2_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab]                   = useState<Tab>("Today's Check-ins");
  const [bookings, setBookings]         = useState<Booking[]>([]);
  const [selectedBookingId, setSelectedBookingId] = useState<string>("");
  const [roomGroups, setRoomGroups]     = useState<RoomGroup[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [loadingPassports, setLoadingPassports] = useState(false);
  const [error, setError]               = useState("");
  const [exportingReport, setExportingReport] = useState(false);

  const fetchBookings = () => {
    setLoadingBookings(true);
    const today = new Date().toISOString().split("T")[0];
    axios.get(`${API_BASE}/api/dashboard/notifications?date=${today}`, { headers })
      .then(r => setBookings(r.data?.todayArrivals || []))
      .catch(() => setBookings([]))
      .finally(() => setLoadingBookings(false));
  };

  useEffect(() => {
    if (tab !== "Guest IDs" && tab !== "Upload ID") return;
    if (bookings.length === 0) fetchBookings();
  }, [tab]);

  useEffect(() => {
    if (!selectedBookingId) { setRoomGroups([]); return; }
    setLoadingPassports(true);
    setError("");
    axios.get(`${API_BASE}/api/passports/booking/${selectedBookingId}`, { headers })
      .then(r => setRoomGroups(Array.isArray(r.data) ? r.data : []))
      .catch(e => setError(e?.response?.data?.error || "Failed to load ID documents"))
      .finally(() => setLoadingPassports(false));
  }, [selectedBookingId]);

  const totalGuests = roomGroups.reduce((s, r) => s + r.guests.length, 0);

  const handlePoliceReport = () => {
    const today = new Date().toISOString().split("T")[0];
    setExportingReport(true);
    const url = `${API_BASE}/api/passports/police-report?date=${today}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(async res => {
        if (!res.ok) { const d = await res.json(); alert(d.error || "No records found"); return; }
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `police-report-${today}.csv`;
        a.click();
      })
      .catch(() => alert("Export failed"))
      .finally(() => setExportingReport(false));
  };

  return (
    <DashboardLayout>
      <div style={{ padding: "24px 28px", maxWidth: 1100 }}>
        <div style={{ marginBottom: 24, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#FDFAF4", margin: 0 }}>Reception</h1>
            <p style={{ fontSize: 13, color: "rgba(242,235,217,0.4)", margin: "4px 0 0" }}>Guest check-in and ID verification</p>
          </div>
          <button
            onClick={handlePoliceReport}
            disabled={exportingReport}
            style={{ padding: "8px 16px", borderRadius: 8, background: "rgba(99,102,241,0.12)", border: "1px solid rgba(99,102,241,0.3)", color: "#818cf8", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            {exportingReport ? "Exporting..." : "Police Report CSV"}
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          {(["Today's Check-ins", "Guest IDs", "Upload ID"] as Tab[]).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              background: "none", border: "none", cursor: "pointer", padding: "10px 16px",
              fontSize: 13, fontWeight: 600, color: tab === t ? "#6366f1" : "#64748b",
              borderBottom: tab === t ? "2px solid #6366f1" : "2px solid transparent",
              marginBottom: -1, transition: "color 0.15s",
            }}>{t}</button>
          ))}
        </div>

        {tab === "Today's Check-ins" && <TodayCheckInsPanel headers={headers} />}

        {tab === "Guest IDs" && (
          <div>
            {/* Booking selector */}
            <div style={{ marginBottom: 24, display: "flex", alignItems: "flex-end", gap: 16 }}>
              <div style={{ flex: 1, maxWidth: 420 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(242,235,217,0.4)", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
                  Select Booking
                </label>
                <select
                  value={selectedBookingId}
                  onChange={e => setSelectedBookingId(e.target.value)}
                  disabled={loadingBookings}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", color: "#F2EBD9", outline: "none", cursor: "pointer" }}
                >
                  <option value="">— Choose a booking —</option>
                  {bookings.map(b => (
                    <option key={b.bookingid} value={b.bookingid}>
                      {b.bookingid} · {b.guestname} · Room {b.roomid}
                    </option>
                  ))}
                </select>
              </div>
              {selectedBookingId && totalGuests > 0 && (
                <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", paddingBottom: 10 }}>
                  {roomGroups.length} room{roomGroups.length > 1 ? "s" : ""} · {totalGuests} guest{totalGuests > 1 ? "s" : ""}
                </div>
              )}
            </div>

            {loadingPassports && <div style={{ color: "rgba(242,235,217,0.4)", fontSize: 13, padding: 20 }}>Loading documents...</div>}
            {error && <div style={{ color: "#f87171", fontSize: 13, padding: 12, background: "rgba(248,113,113,0.08)", borderRadius: 8 }}>{error}</div>}

            {!loadingPassports && !error && selectedBookingId && roomGroups.length === 0 && (
              <div style={{ color: "rgba(242,235,217,0.35)", fontSize: 13, padding: 30, textAlign: "center", background: "rgba(255,255,255,0.02)", borderRadius: 10, border: "1px dashed rgba(255,255,255,0.07)" }}>
                No ID documents submitted for this booking yet.
              </div>
            )}

            {roomGroups.map(room => <RoomSection key={room.roomid} room={room} />)}
          </div>
        )}

        {tab === "Upload ID" && (
          <UploadIDPanel
            headers={headers}
            bookings={bookings}
            loadingBookings={loadingBookings}
            onSaved={() => {
              setTab("Guest IDs");
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
}

function TodayCheckInsPanel({ headers }: { headers: Record<string, string> }) {
  const [checkIns, setCheckIns] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    axios.get(`${API_BASE}/api/dashboard/notifications?date=${today}`, { headers })
      .then(r => setCheckIns(r.data?.todayArrivals || []))
      .catch(() => setCheckIns([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: "rgba(242,235,217,0.4)", fontSize: 13, padding: 20 }}>Loading...</div>;
  if (!checkIns.length) return (
    <div style={{ color: "rgba(242,235,217,0.35)", fontSize: 13, padding: 30, textAlign: "center", background: "rgba(255,255,255,0.02)", borderRadius: 10, border: "1px dashed rgba(255,255,255,0.07)" }}>
      No check-ins scheduled for today.
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {checkIns.map(b => (
        <div key={b.bookingid} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#FDFAF4" }}>{b.guestname}</div>
            <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginTop: 2 }}>{b.bookingid} · Room {b.roomid}</div>
          </div>
          <div style={{ fontSize: 12, color: "rgba(242,235,217,0.3)" }}>{fmt(b.checkin)} → {fmt(b.checkout)}</div>
          <StatusBadge status={b.status} />
        </div>
      ))}
    </div>
  );
}

interface UploadIDPanelProps {
  headers: Record<string, string>;
  bookings: Booking[];
  loadingBookings: boolean;
  onSaved: () => void;
}

type DocType = "national_id" | "passport";

interface SideState {
  file: File | null;
  preview: string | null;
  path: string | null;
  uploading: boolean;
}

const emptySide = (): SideState => ({ file: null, preview: null, path: null, uploading: false });

function UploadIDPanel({ headers, bookings, loadingBookings, onSaved }: UploadIDPanelProps) {
  const [bookingId,  setBookingId]  = useState("");
  const [guestIndex, setGuestIndex] = useState(0);
  const [docType,    setDocType]    = useState<DocType>("national_id");
  const [front,      setFront]      = useState<SideState>(emptySide());
  const [back,       setBack]       = useState<SideState>(emptySide());
  const [saving,     setSaving]     = useState(false);
  const [savedMsg,   setSavedMsg]   = useState("");
  const [err,        setErr]        = useState("");

  const guestName = bookings.find(b => b.bookingid === bookingId)?.guestname || "";

  const pickFile = (side: "front" | "back", f: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const update = { file: f, preview: e.target?.result as string, path: null, uploading: false };
      side === "front" ? setFront(update) : setBack(update);
    };
    reader.readAsDataURL(f);
    setErr(""); setSavedMsg("");
  };

  const uploadSide = async (side: "front" | "back") => {
    const state = side === "front" ? front : back;
    if (!state.file || !bookingId) return;
    const set = side === "front" ? setFront : setBack;
    set(s => ({ ...s, uploading: true }));
    setErr("");
    try {
      const form = new FormData();
      form.append("image", state.file);
      form.append("bookingId", bookingId);
      form.append("guestIndex", String(guestIndex));
      form.append("side", side);
      form.append("guestName", guestName);
      const res = await axios.post(`${API_BASE}/api/passports/upload`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      set(s => ({ ...s, path: res.data.imagePath, uploading: false }));
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Upload failed");
      set(s => ({ ...s, uploading: false }));
    }
  };

  const canSave = bookingId && (
    docType === "passport" ? !!front.path :
    !!(front.path && back.path)
  );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true); setErr("");
    try {
      const booking = bookings.find(b => b.bookingid === bookingId);
      const payload: Record<string, any> = {
        reservationno: bookingId,
        guest_index: guestIndex,
        roomid: booking?.roomid || null,
        guest_name: guestName || null,
        doc_type: docType === "national_id" ? "National ID" : "Passport",
        image_front: front.path || null,
        image_back: back.path || null,
      };
      await axios.post(`${API_BASE}/api/passports`, payload, { headers });
      setSavedMsg("Saved successfully");
      setFront(emptySide()); setBack(emptySide()); setBookingId(""); setGuestIndex(0);
      setTimeout(() => { setSavedMsg(""); onSaved(); }, 1500);
    } catch (e: any) {
      setErr(e?.response?.data?.error || "Save failed");
    } finally { setSaving(false); }
  };

  const inputStyle = { width: "100%", padding: "9px 10px", borderRadius: 8, fontSize: 13, background: "rgba(255,255,255,0.03)", border: "1px solid #e2e8f0", color: "#F2EBD9", outline: "none" };

  const DropZone = ({ side, state }: { side: "front" | "back"; state: SideState }) => (
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(242,235,217,0.4)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
        {side === "front" ? (docType === "national_id" ? "Front Side" : "Passport") : "Back Side"}
      </div>
      <div
        onClick={() => document.getElementById(`id-input-${side}`)?.click()}
        style={{ border: `2px dashed ${state.path ? "#34d399" : "rgba(201,168,76,0.12)"}`, borderRadius: 12, padding: "20px 16px", textAlign: "center", cursor: "pointer", background: state.preview ? "transparent" : "rgba(255,255,255,0.03)", minHeight: 140, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8 }}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) pickFile(side, f); }}
      >
        {state.preview
          ? <img src={state.preview} alt={side} style={{ maxHeight: 120, maxWidth: "100%", borderRadius: 8, objectFit: "contain" }} />
          : <span style={{ fontSize: 12, color: "rgba(242,235,217,0.3)" }}>Click or drag image</span>
        }
        <input id={`id-input-${side}`} type="file" accept="image/*" capture="environment" style={{ display: "none" }}
          onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(side, f); }} />
      </div>
      {state.file && !state.path && (
        <button
          onClick={() => uploadSide(side)}
          disabled={state.uploading || !bookingId}
          style={{ marginTop: 8, width: "100%", padding: "8px 0", borderRadius: 8, background: "#2563eb", border: "none", color: "#0E1E38", fontWeight: 600, fontSize: 12, cursor: state.uploading || !bookingId ? "default" : "pointer", opacity: state.uploading || !bookingId ? 0.5 : 1 }}
        >
          {state.uploading ? "Uploading…" : "Upload"}
        </button>
      )}
      {state.path && <div style={{ marginTop: 6, fontSize: 11, color: "#34d399", fontWeight: 600, textAlign: "center" }}>✓ Uploaded</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Booking + guest + doc type selectors */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(242,235,217,0.4)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Booking</label>
          <select value={bookingId} onChange={e => setBookingId(e.target.value)} disabled={loadingBookings} style={inputStyle}>
            <option value="">— Select booking —</option>
            {bookings.map(b => <option key={b.bookingid} value={b.bookingid}>{b.bookingid} · {b.guestname}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(242,235,217,0.4)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Guest #</label>
          <select value={guestIndex} onChange={e => setGuestIndex(Number(e.target.value))} style={inputStyle}>
            {[0,1,2,3,4].map(i => <option key={i} value={i}>Guest {i + 1}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(242,235,217,0.4)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Document Type</label>
          <select value={docType} onChange={e => { setDocType(e.target.value as DocType); setFront(emptySide()); setBack(emptySide()); }} style={inputStyle}>
            <option value="national_id">National ID (front + back)</option>
            <option value="passport">Passport</option>
          </select>
        </div>
      </div>

      {bookingId && guestName && (
        <div style={{ fontSize: 12, color: "rgba(242,235,217,0.4)", marginBottom: 16 }}>
          Files will be saved as: <strong style={{ color: "#F2EBD9" }}>{guestName.replace(/\s+/g, "_")}_{bookingId}_…</strong>
        </div>
      )}

      {/* Upload zones */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
        <DropZone side="front" state={front} />
        {docType === "national_id" && <DropZone side="back" state={back} />}
      </div>

      {err && <div style={{ color: "#f87171", fontSize: 12, marginBottom: 12, padding: "8px 12px", background: "rgba(248,113,113,0.08)", borderRadius: 8 }}>{err}</div>}
      {savedMsg && <div style={{ color: "#34d399", fontSize: 12, marginBottom: 12, padding: "8px 12px", background: "rgba(52,211,153,0.08)", borderRadius: 8 }}>{savedMsg}</div>}

      <button
        onClick={handleSave}
        disabled={saving || !canSave}
        style={{ padding: "10px 24px", borderRadius: 9, background: canSave ? "#2563eb" : "rgba(201,168,76,0.12)", border: "none", color: canSave ? "#0E1E38" : "#94a3b8", fontWeight: 700, fontSize: 13, cursor: saving || !canSave ? "default" : "pointer" }}
      >
        {saving ? "Saving…" : "Save to Guest Records"}
      </button>
    </div>
  );
}
